import { Sparkles, ArrowRight } from "lucide-react";

/**
 * Contextual "Ask AURI About This" nudge card.
 * Used at the bottom of opportunity + learning-center detail pages.
 *
 * Dispatches a global `altura:ask-auri` event that the AskAuriWidget
 * listens for — no second chat interface, no duplicated state.
 */
export default function AskAuriAboutThis({
  kind = "opportunity",   // "opportunity" | "article" | "resource"
  title,
  slug,
  autosubmit = true,
  testid,
}) {
  const map = {
    opportunity: {
      eyebrow: "Continue with AURI",
      heading: "Have questions about this opportunity?",
      body: "Ask AURI to explain the location, investment context, next steps, or related Altura resources.",
      button: "Ask AURI About This Opportunity",
      prompt: title
        ? `I'm reviewing the Altura opportunity "${title}"${slug ? ` (/opportunities/${slug})` : ""}. Please explain the location, investment context, and what I should explore next.`
        : "Tell me more about this opportunity — location, investment context, and next steps.",
    },
    article: {
      eyebrow: "Continue with AURI",
      heading: "Want to explore this topic further?",
      body: "Ask AURI a follow-up question based on what you just read.",
      button: "Ask AURI About This Article",
      prompt: title
        ? `I'm reading "${title}"${slug ? ` (/learning/${slug})` : ""}. Can you explain the most important points and recommend what I should explore next?`
        : "Explain the most important points of this article and recommend what I should explore next.",
    },
    resource: {
      eyebrow: "Continue with AURI",
      heading: "Have a follow-up on this resource?",
      body: "Ask AURI to summarize the resource or point you to related educational content.",
      button: "Ask AURI About This Resource",
      prompt: title
        ? `I'm reviewing the Altura resource "${title}". Please summarize the key points and recommend what to read next.`
        : "Summarize this resource and recommend what to read next.",
    },
  };
  const cfg = map[kind] || map.opportunity;

  const trigger = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("altura:ask-auri", {
      detail: { prompt: cfg.prompt, autosubmit, kind, slug },
    }));
  };

  return (
    <div className="rounded-2xl border border-altura-gold/40 bg-white p-6 sm:p-8 shadow-[0_1px_2px_rgba(20,20,25,0.04)]" data-testid={testid || `ask-auri-about-${kind}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-full bg-altura-gold/10 text-altura-gold-dark">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-altura-gold-dark">{cfg.eyebrow}</div>
          <div className="mt-1 font-editorial text-lg sm:text-xl text-altura-charcoal">{cfg.heading}</div>
          <p className="mt-2 text-sm text-altura-charcoal/70 leading-relaxed">{cfg.body}</p>
        </div>
        <button
          onClick={trigger}
          className="btn-gold shrink-0 whitespace-nowrap"
          data-testid={`${testid || `ask-auri-about-${kind}`}-btn`}
        >
          {cfg.button} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
